import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  XCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HeartIcon
  // XMarkIcon, UserGroupIcon - reserved for future use
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartIconSolid
} from '@heroicons/react/24/solid';
import { recommendationsApi, itinerariesApi, itineraryGenerationApi } from '../services/api';
import { apiUtils } from '../services/api';
import ItineraryGenerationModal from '../components/generation/ItineraryGenerationModal';
import { getUserId } from '../utils/userId';
import { dnaToPreferences } from '../components/preferences/TravelDNABoard';
import SwipeOnboarding from '../components/onboarding/SwipeOnboarding';
import { showAffirmation } from '../components/shared/AffirmationToast';
// eslint-disable-next-line no-unused-vars
import { generateExpediaLinkFromAccommodation, extractHotelName } from '../utils/expediaAffiliate';
import {
  generateItineraryTripSchema,
  generateItineraryBreadcrumbSchema,
  generateItineraryHowToSchema
} from '../utils/structuredData';
import SEO from '../components/seo/SEO';
import DesignTokens from '../styles/DesignTokens';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ItinerarySplitView from '../components/itinerary/ItinerarySplitView';
import BudgetPlanner from '../components/itinerary/BudgetPlanner';
import UserPreferencesModal from '../components/preferences/UserPreferencesModal';
// import HotelSearchWidget from '../components/HotelSearchWidget';
// import Breadcrumbs from '../components/common/Breadcrumbs';
import { isRecentlyCreated, formatRelativeTime, saveScrollPosition, restoreScrollPosition, clearScrollPosition } from '../utils/itineraryHelpers';
import PlaceAutocomplete from '../components/PlaceAutocomplete'; // eslint-disable-line no-unused-vars
// Feedback components
import RatingButton from '../components/feedback/RatingButton'; // eslint-disable-line no-unused-vars
// import AlternativesPanel from '../components/feedback/AlternativesPanel'; // HIDDEN FOR NOW
// Import extracted modules
import {
  PageContainer, Container, FormContainer, ScenicFormBackground,
  SentenceBuilder, SentenceText, SentenceInput, SentenceSelect,
  TravelStyleGrid, TravelStyleCard, TravelStyleImage, TravelStyleLabel,
  MagicBoxTextarea, GlassFieldLabel, GlassOptionsToggle, GlassOptionsGrid, GlassSelect,
  ExpandedHeader, ExpandedTitle, SectionTitle,
  FieldGroup,
  GenerateButton,
  BrowseAllButton, ToggleViewButton, AvailableItinerariesContainer, SectionHeader, SectionSubtitle,
  ItineraryTypeBadge, ItineraryCardsContainer, ItineraryCarouselContainer, ItineraryCarouselCardsContainer,
  CarouselPrevButton, CarouselNextButton, CountryCard,
  ItineraryCard, ItineraryImage, AIBadgeSmall,
  ItineraryContent, ItineraryCardTitle, ItineraryCardDescription, ItineraryCardMeta, FavoriteButton, // eslint-disable-line no-unused-vars
  LoadingState, LoaderContainer, LoaderDialog, LoaderSpinner, LoaderText,
  LoaderMainText, LoaderSubText, ErrorState
} from './ItineraryPage.styles';
import { BUDGET_OPTIONS, MONTH_OPTIONS, TRANSPORTATION_OPTIONS, ITINERARY_TYPE_OPTIONS, TRAVEL_STYLE_OPTIONS } from './ItineraryPage.constants';
import CountryItinerariesCarousel from './CountryItinerariesCarousel';
import { generateItineraryTagline } from './ItineraryPage.utils';

function ItineraryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: itineraryId, slug: slugParam } = useParams();
  // Support both /itinerary/:slug and /itineraries/:id routes
  const resolvedId = itineraryId || slugParam;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  // Get user ID for limit tracking
  const userId = getUserId();

  // SwipeOnboarding state — show if no travelDNA in localStorage
  const [showSwipeOnboarding, setShowSwipeOnboarding] = useState(() => {
    try { return !localStorage.getItem('travelDNA'); } catch { return false; }
  });

  // Check if user is authenticated (not anonymous)
  const isAuthenticated = userId && !userId.startsWith('user_');
  
  // Carousel refs for each country section
  const countriesCarouselRef = useRef(null);
  const carouselRefs = useRef({
    countriesCarousel: countriesCarouselRef
  });

  // Scroll position persistence - restore scroll when returning to itineraries page
  useEffect(() => {
    const restoreScroll = () => {
      if (!resolvedId) {
        const savedPosition = restoreScrollPosition('itineraries');
        if (savedPosition !== null) {
          window.scrollTo({ top: savedPosition, behavior: 'instant' });
          clearScrollPosition('itineraries');
        }
      }
    };

    // Restore after a short delay to ensure content is rendered
    const timer = setTimeout(restoreScroll, 100);
    return () => clearTimeout(timer);
  }, [resolvedId]);

  // Save scroll position before navigation
  useEffect(() => {
    const handleScroll = () => {
      if (!resolvedId) {
        saveScrollPosition('itineraries', window.scrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [resolvedId]);

  // Fetch itinerary by ID or slug if present in URL
  const { data: fetchedItinerary, isLoading: isLoadingItinerary, error: itineraryError } = useQuery(
    ['itinerary', resolvedId, userId],
    () => itinerariesApi.getById(resolvedId, userId),
    {
      enabled: !!resolvedId,
      select: (response) => {
        const data = response?.data || response;
        // Parse dayByDay JSON if it's a string
        if (data && data.dayByDay && typeof data.dayByDay === 'string') {
          try {
            data.dayByDay = JSON.parse(data.dayByDay);
          } catch (e) {
            data.dayByDay = [];
          }
        }
        // Parse placesCovered JSON if it's a string
        if (data && data.placesCovered && typeof data.placesCovered === 'string') {
          try {
            data.placesCovered = JSON.parse(data.placesCovered);
          } catch (e) {
            data.placesCovered = [];
          }
        }
        // Parse tips JSON if it's a string
        if (data && data.tips && typeof data.tips === 'string') {
          try {
            data.tips = JSON.parse(data.tips);
          } catch (e) {
            data.tips = [];
          }
        }
        return data;
      },
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      onSuccess: (data) => {
        if (data) {
          // Prefill form with itinerary data
          setFormData({
            place: data.country || '',
            itineraryType: data.itineraryType || '',
            budget: data.budgetRange === 'budget' ? 'low' :
                   data.budgetRange === 'mid-range' ? 'medium' :
                   data.budgetRange === 'luxury' ? 'high' : 'any',
            days: data.durationDays || 3,
            travelerProfile: '',
            arrivalTime: 'morning',
            month: '',
            transportation: 'public'
          });
        }
      }
    }
  );
  
  // Get itinerary data from location state, fetched itinerary, or null
  // Priority: location.state (newly created) > fetchedItinerary (from API) > null
  const [itineraryData, setItineraryData] = useState(() => {
    // If we have location.state data, use it (newly created itinerary)
    if (location.state?.itineraryData) {
      return location.state.itineraryData;
    }
    // Otherwise use fetched itinerary if available
    return fetchedItinerary || null;
  });
  
  // Track if this is a newly created itinerary (from navigation state)
  const isNewlyCreated = !!location.state?.itineraryData;
  
  // Ref for scrolling to itinerary content
  const itineraryContentRef = useRef(null);

  // Ref for map container to capture in PDF
  const mapRef = useRef(null);

  // City extracted from Google Maps geocoding (more accurate than text parsing)
  const [extractedCity, setExtractedCity] = useState(null);
  
  // Update itinerary data when fetched itinerary changes
  // Only update if we don't have location.state data (preserve newly created itineraries)
  useEffect(() => {
    if (fetchedItinerary && !location.state?.itineraryData) {
      setItineraryData(fetchedItinerary);
      // Reset extracted city when itinerary changes (will be re-extracted from new map geocoding)
      setExtractedCity(null);
    }
  }, [fetchedItinerary, location.state?.itineraryData]);

  // Redirect numeric ID URLs to slug URLs for canonical URLs (SEO best practice)
  useEffect(() => {
    if (resolvedId && fetchedItinerary && fetchedItinerary.slug) {
      // Check if the current URL is using a numeric ID
      const isNumericId = /^\d+$/.test(resolvedId);

      if (isNumericId) {
        // Redirect to the slug URL (SEO canonical URL)
        navigate(`/itinerary/${fetchedItinerary.slug}`, {
          replace: true,
          state: location.state
        });
      }
    }
  }, [resolvedId, fetchedItinerary, navigate, location.state]);

  // Scroll to and focus on newly created itinerary or when itinerary is loaded by ID
  useEffect(() => {
    if (itineraryData && itineraryContentRef.current && (isNewlyCreated || (resolvedId && fetchedItinerary))) {
      // Small delay to ensure DOM is ready and content is rendered
      const scrollTimeout = setTimeout(() => {
        if (itineraryContentRef.current) {
          // Scroll to top of page first, then to itinerary content
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          setTimeout(() => {
            if (itineraryContentRef.current) {
              itineraryContentRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
              });
              
              // Add a visual highlight effect to draw attention
              itineraryContentRef.current.style.transition = 'box-shadow 0.5s ease';
              itineraryContentRef.current.style.boxShadow = '0 0 0 4px rgba(66, 133, 244, 0.4)';
              
              // Remove highlight after animation
              setTimeout(() => {
                if (itineraryContentRef.current) {
                  itineraryContentRef.current.style.boxShadow = '';
                }
              }, 2500);
            }
          }, 100);
        }
      }, 400);
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [isNewlyCreated, itineraryData, resolvedId, fetchedItinerary]);

  // Clear itinerary data when navigating away from a specific itinerary (resolvedId becomes null)
  // But preserve location.state data (newly created itineraries)
  useEffect(() => {
    if (!resolvedId && itineraryData && !isLoadingItinerary && !location.state?.itineraryData) {
      // Clear itinerary data when we navigate away from a specific itinerary
      // But don't clear if we have location.state data (newly created)
      setItineraryData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId, isLoadingItinerary, location.state?.itineraryData]);
  
  const [formData, setFormData] = useState({
    place: searchParams.get('place') || searchParams.get('country') || location.state?.formData?.place || '',
    budget: searchParams.get('budget') || location.state?.formData?.budget || 'any',
    days: parseInt(searchParams.get('days')) || location.state?.formData?.days || 3,
    itineraryType: searchParams.get('itineraryType') || searchParams.get('mood') || searchParams.get('type') || location.state?.formData?.itineraryType || location.state?.formData?.mood || '',
    travelerProfile: searchParams.get('travelerProfile') || location.state?.formData?.travelerProfile || '',
    arrivalTime: searchParams.get('arrivalTime') || location.state?.formData?.arrivalTime || 'morning',
    month: searchParams.get('month') || location.state?.formData?.month || '',
    transportation: searchParams.get('transportation') || location.state?.formData?.transportation || 'public'
  });

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(true);

  // State for transparent generation flow
  const [generationJobId, setGenerationJobId] = useState(null);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [showBudgetPlanner, setShowBudgetPlanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);
  const [estimatedDuration, setEstimatedDuration] = useState({ min: 30, max: 60 }); // Dynamic based on trip duration

  // Debounced place value for backend queries (prevents API calls on every keystroke)
  const [debouncedPlace, setDebouncedPlace] = useState(formData.place);

  // Debounce formData.place changes (500ms delay, matches Google Places Autocomplete)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPlace(formData.place);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.place]);

  // State for selected country filter
  const [selectedCountryFilter, setSelectedCountryFilter] = useState(null);
  
  // State for expanded country (to show its itineraries)
  const [expandedCountry, setExpandedCountry] = useState(null);

  // Check itinerary limit
  const { data: limitStatus, refetch: refetchLimit } = useQuery(
    ['itineraryLimit', userId],
    () => recommendationsApi.checkItineraryLimit(userId),
    {
      enabled: !!userId && !itineraryData, // Only check if no itinerary yet
      select: (response) => response?.data || response,
      retry: 1,
      staleTime: 30 * 1000, // Cache for 30 seconds
    }
  );

  // Fetch existing itineraries for the entered country
  // Uses debounced place value to prevent API calls on every keystroke
  // Only triggers after user has typed at least 3 characters (matches Google Places Autocomplete minimum)
  const { data: existingItineraries } = useQuery(
    ['existingItineraries', debouncedPlace],
    () => itinerariesApi.getByCountry(debouncedPlace, userId),
    {
      enabled: !!debouncedPlace && debouncedPlace.trim().length >= 3 && !itineraryData && !resolvedId,
      select: (response) => {
        const data = response?.data || response;
        return Array.isArray(data) ? data : [];
      },
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      onError: (error) => {
        // Silently fail - no itineraries found
      }
    }
  );

  // Fetch all countries with itinerary counts
  const { data: countriesWithCounts, isLoading: isLoadingCountries } = useQuery(
    ['countriesWithCounts'],
    () => itinerariesApi.getCountriesWithCounts(),
    {
      enabled: !itineraryData && !resolvedId,
      select: (response) => {
        const data = response?.data || response;
        return Array.isArray(data) ? data : [];
      },
      retry: 1,
      staleTime: 10 * 60 * 1000, // Cache for 10 minutes
      onError: (error) => {
        // Silently handle error
      }
    }
  );

  // Fetch featured itineraries to show available itineraries
  const { data: featuredItineraries, isLoading: isLoadingFeatured } = useQuery(
    ['featuredItineraries', userId],
    () => itinerariesApi.getFeatured(userId),
    {
      enabled: !itineraryData && !resolvedId,
      select: (data) => {
        try {
          return Array.isArray(data.data) ? data.data : [];
        } catch (e) {
          // Error parsing featured itineraries
          return [];
        }
      },
      onError: (error) => {
        // Failed to fetch featured itineraries
      }
    }
  );

  // Fetch saved/favorited itineraries (only for authenticated users)
  const { data: savedItineraries } = useQuery(
    ['savedItineraries', userId],
    () => itinerariesApi.getFavorites(userId),
    {
      enabled: isAuthenticated && !!userId && !itineraryData && !resolvedId,
      select: (response) => {
        const data = response?.data || response;
        return Array.isArray(data) ? data : [];
      },
      retry: 1,
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes (shorter than others since favorites change often)
      onError: (error) => {
        // Failed to fetch saved itineraries
      }
    }
  );

  // Fetch user's created itineraries (only for authenticated users)
  const { data: createdItineraries } = useQuery(
    ['createdItineraries', userId],
    () => itinerariesApi.getCreatedByUser(userId, 10),
    {
      enabled: isAuthenticated && !!userId && !itineraryData && !resolvedId,
      select: (response) => {
        const data = response?.data || response;
        return Array.isArray(data) ? data : [];
      },
      retry: 1,
      staleTime: 2 * 60 * 1000,
      onError: (error) => {
        // Failed to fetch created itineraries
      }
    }
  );

  // Fetch user statistics (only for authenticated users)
  const { data: userStats } = useQuery(
    ['userStats', userId],
    () => itinerariesApi.getUserStats(userId),
    {
      enabled: isAuthenticated && !!userId && !itineraryData && !resolvedId,
      select: (response) => {
        const data = response?.data || response;
        return data || {};
      },
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      onError: (error) => {
        // Failed to fetch user stats
      }
    }
  );

  // Fetch ALL itineraries (for "Browse All" view)
  const [showBrowseAll, setShowBrowseAll] = useState(false);

  // View mode state: 'default' or 'myItineraries' - Default to myItineraries if authenticated, else default
  const [viewMode, setViewMode] = useState(isAuthenticated ? 'myItineraries' : 'default');

  // Modal state for mobile form
  // showFormModal removed — FAB now switches to browse view

  // Track if viewport is mobile for FAB visibility
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show/hide Your Journey banner
  const [showJourneyBanner, setShowJourneyBanner] = useState(true);

  // Show/hide "No Itineraries" message
  const [showNoItinerariesMessage, setShowNoItinerariesMessage] = useState(true);

  // Auto-hide banner after 8 seconds
  useEffect(() => {
    if (showJourneyBanner && userId && userStats && (userStats.totalCreated > 0 || userStats.createdThisMonth > 0)) {
      const timer = setTimeout(() => {
        setShowJourneyBanner(false);
      }, 8000); // 8 seconds
      return () => clearTimeout(timer);
    }
  }, [showJourneyBanner, userId, userStats]);

  // Set view mode based on authentication, itinerary status, and query params
  useEffect(() => {
    if (searchParams.get('create') === 'true' || location.state?.showForm) {
      // Navigated here to create — always show form
      setViewMode('default');
    } else if (!isAuthenticated) {
      // Not authenticated → show form (default mode)
      setViewMode('default');
    } else {
      // Authenticated → show My Itineraries (even if empty, message will show)
      setViewMode('myItineraries');
    }
  }, [isAuthenticated, createdItineraries, searchParams, location.state]);

  // Auto-generate itinerary when navigated from DestinationDiscoveryPage
  const didAutoGenerate = React.useRef(false);
  useEffect(() => {
    if (location.state?.autoGenerate && !didAutoGenerate.current) {
      didAutoGenerate.current = true;
      // Small delay to allow form state to settle before triggering generation
      const timer = setTimeout(() => {
        handleSubmit({ preventDefault: () => {} });
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.autoGenerate]);
  const { data: allItineraries, isLoading: isLoadingAllItineraries, refetch: refetchAllItineraries } = useQuery(
    ['allItineraries', userId],
    () => itinerariesApi.getAll(50, userId),
    {
      enabled: showBrowseAll && !itineraryData && !resolvedId,
      select: (response) => {
        const data = response?.data || response;
        return Array.isArray(data) ? data : [];
      },
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      onError: (error) => {
        // Failed to fetch all itineraries
      }
    }
  );

  // Favorite toggle mutation for main itinerary view
  const mainFavoriteMutation = useMutation(
    ({ itineraryId }) => {
      return itinerariesApi.toggleFavorite(itineraryId, userId);
    },
    {
      onSuccess: (response, variables) => {
        const { isFavorited, favoriteCount } = response.data;
        // Update itinerary data state
        setItineraryData(prev => prev ? {
          ...prev,
          isFavoritedByUser: isFavorited,
          favoriteCount: favoriteCount
        } : null);
        
        // Update cache for this itinerary
        queryClient.setQueryData(['itinerary', variables.itineraryId, userId], (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            isFavoritedByUser: isFavorited,
            favoriteCount: favoriteCount
          };
        });
        
        // Update all other caches
        const updateCache = (queryKey, oldData) => {
          if (!oldData) return oldData;
          const data = Array.isArray(oldData) ? oldData : (oldData?.data || []);
          return data.map(itinerary => 
            itinerary.id === variables.itineraryId
              ? { ...itinerary, isFavoritedByUser: isFavorited, favoriteCount }
              : itinerary
          );
        };
        
        if (itineraryData?.country) {
          queryClient.setQueryData(['countryItineraries', itineraryData.country, userId], updateCache);
          queryClient.setQueryData(['existingItineraries', itineraryData.country], updateCache);
        }
        queryClient.setQueryData(['featuredItineraries', userId], (oldData) => {
          if (!oldData) return oldData;
          const data = Array.isArray(oldData) ? oldData : (oldData?.data || []);
          return data.map(itinerary => 
            itinerary.id === variables.itineraryId
              ? { ...itinerary, isFavoritedByUser: isFavorited, favoriteCount }
              : itinerary
          );
        });
        
        // Refetch saved itineraries to update the list
        queryClient.invalidateQueries(['savedItineraries', userId]);
        
        toast.success(isFavorited ? 'Added to favorites' : 'Removed from favorites');
      },
      onError: (error) => {
        toast.error('Failed to update favorite');
        // Error toggling favorite
      }
    }
  );

  // Generate itinerary mutation (only when user clicks button)
  const itineraryMutation = useMutation(
    async (data) => {
      const response = await recommendationsApi.generateItinerary(data);
      // Handle nested response structure: response.data.data or response.data
      const responseData = response?.data?.data || response?.data || response;

      // Extract the first itinerary from the array if response contains itineraries array
      const itinerary = responseData?.itineraries?.[0] || responseData;
      return itinerary;
    },
    {
      onSuccess: (itinerary) => {
        // Refresh limit status after successful generation
        refetchLimit();

        // Extract slug or ID from the itinerary response
        // The response might have slug directly or nested
        const itinerarySlug = itinerary?.slug;
        const itineraryId = itinerary?.id;

        if (itinerarySlug || itineraryId) {
          const itineraryUrl = itinerarySlug || itineraryId;
          toast.success(`Generated ${itinerary?.days || itinerary?.durationDays || 0}-day itinerary!`);

          // Navigate to the newly created itinerary immediately with state
          // Pass itinerary data in state so it's available instantly without API call
          navigate(`/itinerary/${itineraryUrl}`, {
            state: { itineraryData: itinerary },
            replace: false
          });
        } else {
          // Fallback: update URL with itinerary ID if slug is not available
          toast.success(`Generated ${itinerary?.days || itinerary?.durationDays || 0}-day itinerary!`);
          if (itineraryId) {
            setItineraryData(itinerary);
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set('id', itineraryId);
              return newParams;
            });
          }
        }
      },
      onError: (error) => {
        const errorInfo = apiUtils.handleError(error);
        let errorMessage = errorInfo.message || 'Failed to generate itinerary. Please try again.';
        
        // Check for 400 Bad Request (validation error)
        if (error?.response?.status === 400) {
          const errorData = error?.response?.data;
          
          // Check if there are specific field validation errors
          if (errorData?.errors && typeof errorData.errors === 'object') {
            // Extract the first validation error message
            const firstError = Object.values(errorData.errors)[0];
            if (firstError) {
              errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
            } else {
              // Fallback to generic validation message
              errorMessage = errorData.message || 'Please check your input and try again.';
            }
          } else if (errorData?.message) {
            // Use the error message from the API
            errorMessage = errorData.message;
          } else {
            // Generic validation error fallback
            errorMessage = 'Please check your input and try again.';
          }
        }
        
        // Check if it's a limit error
        if (errorMessage.includes('limit') || errorMessage.includes('24 hours')) {
          refetchLimit(); // Refresh limit status
        }
        
        toast.error(errorMessage);
      }
    }
  );

  // No auto-generation - user must click button

  // Copy link to clipboard
  const handleCopyLink = () => { // eslint-disable-line no-unused-vars
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  // Download as PDF
  const handleDownloadPDF = async () => { // eslint-disable-line no-unused-vars
    if (!itineraryData) {
      toast.error('No itinerary data available to download');
      return;
    }

    try {
      toast.info('Generating PDF... Please wait');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = margin;

      // Title
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      const title = `${itineraryData.durationDays || itineraryData.days || 0}-Day Itinerary for ${itineraryData.country || 'Your Destination'}`;
      const titleLines = doc.splitTextToSize(title, pageWidth - 2 * margin);
      doc.text(titleLines, margin, yPos);
      yPos += titleLines.length * 8 + 10;

      // Trip Summary / Description
      const summary = itineraryData.description || itineraryData.tripSummary;
      if (summary) {
        if (yPos > 250) {
          doc.addPage();
          yPos = margin;
        }
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Trip Summary', margin, yPos);
        yPos += 8;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const summaryLines = doc.splitTextToSize(summary, pageWidth - 2 * margin);
        doc.text(summaryLines, margin, yPos);
        yPos += summaryLines.length * 5 + 10;
      }

      // Capture and add map image
      if (mapRef.current) {
        try {
          // Add a new page for the map
          doc.addPage();
          yPos = margin;

          // Map section title
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('Itinerary Map', margin, yPos);
          yPos += 10;

          // Capture the map element
          const canvas = await html2canvas(mapRef.current, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            scale: 2, // Higher quality
            logging: false
          });

          const imgData = canvas.toDataURL('image/png');

          // Calculate dimensions to fit the map on the page
          const imgWidth = pageWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Add the map image to PDF
          doc.addImage(imgData, 'PNG', margin, yPos, imgWidth, imgHeight);

          // Reset yPos for next page
          yPos = margin;
        } catch (mapError) {
          console.error('Error capturing map:', mapError);
          // Continue with PDF generation even if map capture fails
        }
      }

      // Add new page for daily itinerary
      doc.addPage();
      yPos = margin;

      // Daily Itinerary - Handle both dayByDay and dailyItinerary formats
      const days = itineraryData.dailyItinerary || itineraryData.dayByDay || [];
      
      if (days && days.length > 0) {
        days.forEach((day, index) => {
          // Check if we need a new page
          if (yPos > 250) {
            doc.addPage();
            yPos = margin;
          }

          // Day Header
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          const dayNumber = day.day || index + 1;
          const dayTitle = day.title || `Day ${dayNumber}`;
          doc.text(dayTitle, margin, yPos);
          yPos += 10;

          // Handle new dayByDay format
          if (itineraryData.dayByDay) {
            // Activities
            if (day.activities && Array.isArray(day.activities) && day.activities.length > 0) {
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Activities', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              day.activities.forEach(activity => {
                if (yPos > 270) {
                  doc.addPage();
                  yPos = margin;
                }
                const activityText = typeof activity === 'string' ? activity : activity.activity || activity.name || '';
                if (activityText) {
                  doc.setFont(undefined, 'bold');
                  doc.text(`• ${activityText}`, margin + 5, yPos);
                  yPos += 6;
                  const activityDesc = typeof activity === 'object' ? activity.description : '';
                  if (activityDesc) {
                    doc.setFont(undefined, 'normal');
                    const descLines = doc.splitTextToSize(activityDesc, pageWidth - 2 * margin - 10);
                    doc.text(descLines, margin + 5, yPos);
                    yPos += descLines.length * 5;
                  }
                  yPos += 3;
                }
              });
            }

            // Places
            if (day.places && Array.isArray(day.places) && day.places.length > 0) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Places to Visit', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              day.places.forEach(place => {
                if (yPos > 270) {
                  doc.addPage();
                  yPos = margin;
                }
                const placeName = typeof place === 'string' ? place : place.name || place;
                doc.text(`• ${placeName}`, margin + 5, yPos);
                yPos += 5;
              });
              yPos += 3;
            }

            // Meals
            if (day.meals) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Meals', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              const mealsLines = doc.splitTextToSize(day.meals, pageWidth - 2 * margin - 10);
              doc.text(mealsLines, margin + 5, yPos);
              yPos += mealsLines.length * 5 + 5;
            }

            // Accommodation
            if (day.accommodation) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Accommodation', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              const accLines = doc.splitTextToSize(day.accommodation, pageWidth - 2 * margin - 10);
              doc.text(accLines, margin + 5, yPos);
              yPos += accLines.length * 5 + 5;
            }
          } else {
            // Handle old dailyItinerary format (morning, afternoon, evening)
            // Morning
            if (day.morning && day.morning.length > 0) {
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Morning', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              day.morning.forEach(activity => {
                if (yPos > 270) {
                  doc.addPage();
                  yPos = margin;
                }
                const activityText = typeof activity === 'string' ? activity : activity.activity || '';
                if (activityText) {
                  doc.setFont(undefined, 'bold');
                  doc.text(`• ${activityText}`, margin + 5, yPos);
                  yPos += 6;
                  const activityDesc = typeof activity === 'object' ? activity.description : '';
                  if (activityDesc) {
                    doc.setFont(undefined, 'normal');
                    const descLines = doc.splitTextToSize(activityDesc, pageWidth - 2 * margin - 10);
                    doc.text(descLines, margin + 5, yPos);
                    yPos += descLines.length * 5;
                  }
                  yPos += 3;
                }
              });
            }

            // Afternoon
            if (day.afternoon && day.afternoon.length > 0) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Afternoon', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              day.afternoon.forEach(activity => {
                if (yPos > 270) {
                  doc.addPage();
                  yPos = margin;
                }
                const activityText = typeof activity === 'string' ? activity : activity.activity || '';
                if (activityText) {
                  doc.setFont(undefined, 'bold');
                  doc.text(`• ${activityText}`, margin + 5, yPos);
                  yPos += 6;
                  const activityDesc = typeof activity === 'object' ? activity.description : '';
                  if (activityDesc) {
                    doc.setFont(undefined, 'normal');
                    const descLines = doc.splitTextToSize(activityDesc, pageWidth - 2 * margin - 10);
                    doc.text(descLines, margin + 5, yPos);
                    yPos += descLines.length * 5;
                  }
                  yPos += 3;
                }
              });
            }

            // Evening
            if (day.evening && day.evening.length > 0) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Evening', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              day.evening.forEach(activity => {
                if (yPos > 270) {
                  doc.addPage();
                  yPos = margin;
                }
                const activityText = typeof activity === 'string' ? activity : activity.activity || '';
                if (activityText) {
                  doc.setFont(undefined, 'bold');
                  doc.text(`• ${activityText}`, margin + 5, yPos);
                  yPos += 6;
                  const activityDesc = typeof activity === 'object' ? activity.description : '';
                  if (activityDesc) {
                    doc.setFont(undefined, 'normal');
                    const descLines = doc.splitTextToSize(activityDesc, pageWidth - 2 * margin - 10);
                    doc.text(descLines, margin + 5, yPos);
                    yPos += descLines.length * 5;
                  }
                  yPos += 3;
                }
              });
            }

            // Meals (old format)
            if (day.meals) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Meals', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              const mealsText = typeof day.meals === 'string' ? day.meals : '';
              if (mealsText) {
                const mealsLines = doc.splitTextToSize(mealsText, pageWidth - 2 * margin - 10);
                doc.text(mealsLines, margin + 5, yPos);
                yPos += mealsLines.length * 5 + 5;
              }
            }

            // Accommodation (old format)
            if (day.accommodation) {
              if (yPos > 250) {
                doc.addPage();
                yPos = margin;
              }
              doc.setFontSize(12);
              doc.setFont(undefined, 'bold');
              doc.text('Accommodation', margin, yPos);
              yPos += 7;
              doc.setFont(undefined, 'normal');
              doc.setFontSize(10);
              const accText = typeof day.accommodation === 'string' ? day.accommodation : '';
              if (accText) {
                const accLines = doc.splitTextToSize(accText, pageWidth - 2 * margin - 10);
                doc.text(accLines, margin + 5, yPos);
                yPos += accLines.length * 5 + 5;
              }
            }
          }

          yPos += 10;
        });
      }

      // Tips
      const tips = itineraryData.tips || itineraryData.packingAndSafetyTips || [];
      if (tips && tips.length > 0) {
        if (yPos > 250) {
          doc.addPage();
          yPos = margin;
        }
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Travel Tips & Recommendations', margin, yPos);
        yPos += 10;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const tipsArray = Array.isArray(tips) ? tips : [tips];
        tipsArray.forEach(tip => {
          if (yPos > 270) {
            doc.addPage();
            yPos = margin;
          }
          const tipText = typeof tip === 'string' ? tip : '';
          if (tipText) {
            const tipLines = doc.splitTextToSize(`• ${tipText}`, pageWidth - 2 * margin);
            doc.text(tipLines, margin, yPos);
            yPos += tipLines.length * 5 + 3;
          }
        });
      }

      // Best Time to Visit
      if (itineraryData.bestTimeToVisit) {
        if (yPos > 250) {
          doc.addPage();
          yPos = margin;
        }
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Best Time to Visit', margin, yPos);
        yPos += 10;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const bestTimeLines = doc.splitTextToSize(itineraryData.bestTimeToVisit, pageWidth - 2 * margin);
        doc.text(bestTimeLines, margin, yPos);
        yPos += bestTimeLines.length * 5 + 10;
      }

      // Worth Visiting (Special Recommendations)
      const specialRecs = itineraryData.specialRecommendations || itineraryData.special_recommendations || [];
      if (specialRecs && specialRecs.length > 0) {
        // Add new page for Worth Visiting section
        doc.addPage();
        yPos = margin;

        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('⭐ Worth Visiting', margin, yPos);
        yPos += 12;

        doc.setFontSize(10);
        doc.setFont(undefined, 'italic');
        const introText = 'Iconic places that didn\'t fit in the itinerary but are definitely worth a visit!';
        const introLines = doc.splitTextToSize(introText, pageWidth - 2 * margin);
        doc.text(introLines, margin, yPos);
        yPos += introLines.length * 5 + 8;

        specialRecs.forEach((rec, index) => {
          // Check if we need a new page
          if (yPos > 240) {
            doc.addPage();
            yPos = margin;
          }

          // Place name and type
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          const placeName = `${index + 1}. ${rec.name}${rec.placeType ? ` - ${rec.placeType}` : ''}`;
          const placeNameLines = doc.splitTextToSize(placeName, pageWidth - 2 * margin);
          doc.text(placeNameLines, margin, yPos);
          yPos += placeNameLines.length * 6 + 3;

          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');

          // Why famous
          if (rec.whyFamous) {
            const whyFamousLines = doc.splitTextToSize(rec.whyFamous, pageWidth - 2 * margin - 5);
            doc.text(whyFamousLines, margin + 5, yPos);
            yPos += whyFamousLines.length * 5 + 3;
          }

          // Details in compact format
          const details = [];
          if (rec.bestTimeToVisit) details.push(`Best Time: ${rec.bestTimeToVisit}`);
          if (rec.estimatedDuration) details.push(`Duration: ${rec.estimatedDuration}`);
          if (rec.estimatedCost) details.push(`Cost: ${rec.estimatedCost}`);

          if (details.length > 0) {
            doc.setFont(undefined, 'italic');
            details.forEach(detail => {
              if (yPos > 270) {
                doc.addPage();
                yPos = margin;
              }
              doc.text(detail, margin + 5, yPos);
              yPos += 5;
            });
            doc.setFont(undefined, 'normal');
            yPos += 2;
          }

          // Getting there
          if (rec.gettingThere) {
            if (yPos > 265) {
              doc.addPage();
              yPos = margin;
            }
            doc.setFont(undefined, 'bold');
            doc.text('Getting There:', margin + 5, yPos);
            yPos += 5;
            doc.setFont(undefined, 'normal');
            const gettingThereLines = doc.splitTextToSize(rec.gettingThere, pageWidth - 2 * margin - 10);
            doc.text(gettingThereLines, margin + 5, yPos);
            yPos += gettingThereLines.length * 5 + 2;
          }

          // Insider tip
          if (rec.insiderTip) {
            if (yPos > 265) {
              doc.addPage();
              yPos = margin;
            }
            doc.setFont(undefined, 'bold');
            doc.text('💡 Insider Tip:', margin + 5, yPos);
            yPos += 5;
            doc.setFont(undefined, 'normal');
            const tipLines = doc.splitTextToSize(rec.insiderTip, pageWidth - 2 * margin - 10);
            doc.text(tipLines, margin + 5, yPos);
            yPos += tipLines.length * 5 + 3;
          }

          // Add spacing between recommendations
          yPos += 5;
        });
      }

      // Save PDF
      const fileName = `itinerary-${(itineraryData.country || 'destination').replace(/\s+/g, '-').toLowerCase()}-${itineraryData.durationDays || itineraryData.days || 0}days.pdf`;
      doc.save(fileName);
      toast.success('Itinerary downloaded as PDF!');
    } catch (error) {
      // Error generating PDF
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const TRAVELER_PROFILE_MAX = 250;

  const sanitizeTravelerProfile = (value) => {
    return value
      .replace(/<[^>]*>/g, '')                              // strip HTML/XML tags
      .replace(/javascript\s*:/gi, '')                      // strip javascript: protocol
      .replace(/on\w+\s*=\s*["']?[^"'\s]*["']?/gi, '')     // strip inline event handlers
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')   // strip non-printable control chars
      .slice(0, TRAVELER_PROFILE_MAX);
  };

  const SQL_INJECTION_PATTERN = /(\b(union|select|insert|update|delete|drop|create|alter|exec|execute)\b.{0,20}\b(from|into|table|where|database)\b|--|\/\*|\*\/|;\s*\w|\bor\s+1\s*=\s*1\b)/i;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'travelerProfile' ? sanitizeTravelerProfile(value) : value
    }));
    // Trigger affirmation on meaningful selections
    if (name === 'itineraryType' || name === 'travelerProfile' || name === 'budgetRange') {
      showAffirmation();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Block if generation is already in progress
    if (generationJobId && showGenerationModal) {
      toast.info('Generation already in progress');
      return;
    }

    // Check limit before submitting
    if (limitStatus && !limitStatus.canCreate) {
      toast.error(limitStatus.message || 'You\'ve reached the limit of 3 itineraries per 24 hours. Please try again later.');
      return;
    }

    // Validate days before submitting (max 10 days)
    const daysValue = formData.days ? parseInt(String(formData.days), 10) : null;
    if (!daysValue || isNaN(daysValue) || daysValue < 1 || daysValue > 10) {
      toast.error('Please enter a valid number of days (1-10)');
      return;
    }

    if (!formData.place || formData.place.trim() === '' || formData.place === 'any') {
      toast.error('Please enter a specific destination (country or city) for itinerary generation');
      return;
    }

    if (formData.travelerProfile && SQL_INJECTION_PATTERN.test(formData.travelerProfile)) {
      toast.error('Please describe your travel preferences in plain text without special commands.');
      return;
    }

    try {
      // Start async generation with transparent progress
      // Send full place string (e.g., "Berlin, Germany" or "France")
      // Backend will intelligently parse city/country
      // Keep free-text traveler profile as-is (preferences now sent as structured data)
      const travelerProfileText = (formData.travelerProfile || '').trim() || null;

      // Read Travel DNA from localStorage and convert to structured preferences
      let travelPreferences = null;
      let travelPersonality = null;
      try {
        const rawDNA = localStorage.getItem('travelDNA');
        if (rawDNA) {
          const dna = JSON.parse(rawDNA);
          if (dna?.dimensions) {
            travelPreferences = dnaToPreferences(dna.dimensions);
            travelPersonality = dna.personality || null;
          }
        }
      } catch (_) {
        // Silently ignore malformed localStorage data
      }

      // Fall back to modal slider values if no DNA stored
      if (!travelPreferences && userPreferences) {
        travelPreferences = {
          activityDensity: userPreferences.activityDensity,
          pace: userPreferences.pace,
          walkingTolerance: userPreferences.walkingTolerance,
          historyCulture: userPreferences.historyCulture,
          nature: userPreferences.nature,
          foodDining: userPreferences.foodDining,
          nightlife: userPreferences.nightlife,
        };
      }

      const response = await itineraryGenerationApi.startGeneration(
        formData.place.trim(),            // place (can be "City, Country" or just "Country")
        daysValue,
        formData.itineraryType || 'cultural',
        formData.budget || 'medium',
        userId,
        travelerProfileText,
        travelPreferences,
        travelPersonality
      );

      setGenerationJobId(response.data.jobId);

      // Set dynamic estimated duration from backend
      if (response.data.estimatedDurationMin && response.data.estimatedDurationMax) {
        setEstimatedDuration({
          min: response.data.estimatedDurationMin,
          max: response.data.estimatedDurationMax
        });
      }

      setShowGenerationModal(true);

      // Clear existing itinerary when generating new one
      setItineraryData(null);
    } catch (error) {
      console.error('Failed to start itinerary generation:', error);
      if (error.response && error.response.status === 429) {
        toast.error(error.response.data?.error || 'Too many requests. Please try again later.');
      } else {
        toast.error('Failed to start itinerary generation. Please try again.');
      }
    }
  };


  return (
    <>
      {/* SwipeOnboarding overlay when no travelDNA exists */}
      <AnimatePresence>
        {showSwipeOnboarding && (
          <SwipeOnboarding
            onComplete={() => setShowSwipeOnboarding(false)}
            onClose={() => setShowSwipeOnboarding(false)}
          />
        )}
      </AnimatePresence>

      <SEO
        title={itineraryData 
          ? `${itineraryData.title || `${itineraryData.durationDays || itineraryData.days || 0}-Day ${itineraryData.itineraryType || 'Tour'} of ${itineraryData.country}`} | AI Travel Itinerary`
          : 'Browse AI-Powered Travel Itineraries | Safar Glimpse'
        }
        description={itineraryData 
          ? (itineraryData.description || itineraryData.tripSummary || `Explore a ${itineraryData.durationDays}-day ${itineraryData.itineraryType} itinerary through ${itineraryData.country}. AI-generated travel plan with day-by-day activities, accommodation, and local tips.`)
          : 'Discover AI-generated travel itineraries for destinations worldwide. Get personalized day-by-day plans, accommodation recommendations, and local insights.'
        }
        keywords={itineraryData 
          ? `${itineraryData.country} itinerary, ${itineraryData.durationDays} days ${itineraryData.country}, ${itineraryData.itineraryType} trip, AI travel planner, ${itineraryData.country} travel guide`
          : 'AI itinerary, travel planner, trip planning, AI travel recommendations, vacation planning'
        }
        canonical={itineraryData && itineraryData.slug
          ? `https://safarglimpse.com/itinerary/${itineraryData.slug}`
          : undefined
        }
        image={itineraryData?.id
          ? `https://safar-glimpse-service.fly.dev/api/v1/og/itinerary/${itineraryData.id}`
          : undefined
        }
        noindex={!itineraryData || !itineraryData.slug} // Only index pages with slugs (detail pages)
        structuredData={itineraryData ? [
          generateItineraryTripSchema(itineraryData),
          generateItineraryBreadcrumbSchema(itineraryData),
          generateItineraryHowToSchema(itineraryData)
        ].filter(Boolean) : undefined}
      />
      <PageContainer $noNavbar={!!resolvedId}>
        {!resolvedId && !itineraryData ? (
          // Show form when no itinerary
          <>
          <ScenicFormBackground>

          {/* View Mode Navigation - Show for all authenticated users */}
          {isAuthenticated && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                display: 'flex',
                gap: DesignTokens.spacing.sm,
                marginBottom: DesignTokens.spacing.lg,
                padding: '4px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: DesignTokens.borderRadius.md,
                border: '1px solid rgba(255,255,255,0.2)',
                maxWidth: '720px',
                width: '100%'
              }}
            >
              <motion.button
                onClick={() => setViewMode('default')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: viewMode === 'default' ? 'rgba(255,255,255,0.2)' : 'transparent',
                  color: viewMode === 'default' ? 'white' : 'rgba(255,255,255,0.6)',
                  fontWeight: viewMode === 'default' ? '600' : '500',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'default' ? '0 1px 3px rgba(0, 0, 0, 0.2)' : 'none',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <SparklesIcon style={{ width: '16px', height: '16px' }} />
                Create Itinerary
              </motion.button>
              <motion.button
                onClick={() => setViewMode('myItineraries')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: viewMode === 'myItineraries' ? 'rgba(255,255,255,0.2)' : 'transparent',
                  color: viewMode === 'myItineraries' ? 'white' : 'rgba(255,255,255,0.6)',
                  fontWeight: viewMode === 'myItineraries' ? '600' : '500',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'myItineraries' ? '0 1px 3px rgba(0, 0, 0, 0.2)' : 'none',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <MapPinIcon style={{ width: '16px', height: '16px' }} />
                My Itineraries ({createdItineraries?.length || 0})
              </motion.button>
            </motion.div>
          )}

          {/* Show compass loader when generating, otherwise show form */}
          {!resolvedId && !itineraryData && itineraryMutation.isLoading ? (
            <LoaderContainer>
              <LoaderDialog>
                <LoaderSpinner />
                <LoaderText>
                  <LoaderMainText>Generating your Gold Standard Itinerary...</LoaderMainText>
                  <LoaderSubText>Discovering hidden gems...</LoaderSubText>
                  <LoaderSubText>Adding your magic...</LoaderSubText>
                </LoaderText>
              </LoaderDialog>
            </LoaderContainer>
          ) : !resolvedId && !itineraryData && viewMode === 'default' && (
            <>
              <AnimatePresence>
                {userId && userStats && (userStats.totalCreated > 0 || userStats.createdThisMonth > 0) && showJourneyBanner && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10, height: 0, marginBottom: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      padding: DesignTokens.spacing.md,
                      background: 'linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)',
                      borderRadius: DesignTokens.borderRadius.md,
                      marginBottom: DesignTokens.spacing.lg,
                      border: '2px solid #D8B4FE',
                      textAlign: 'center',
                      position: 'relative'
                    }}
                  >
                    {/* Close button */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowJourneyBanner(false)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#9333EA',
                        opacity: 0.6
                      }}
                    >
                      <XCircleIcon style={{ width: '20px', height: '20px' }} />
                    </motion.button>

                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#7C3AED', marginBottom: '4px' }}>
                      Your Journey
                    </div>
                    <div style={{ fontSize: '13px', color: '#6B21A8' }}>
                      {userStats.totalCreated > 0 && (
                        <span>
                          <strong>{userStats.totalCreated}</strong> {userStats.totalCreated === 1 ? 'itinerary' : 'itineraries'} created
                        </span>
                      )}
                      {userStats.totalCreated > 0 && userStats.createdThisMonth > 0 && ' • '}
                      {userStats.createdThisMonth > 0 && (
                        <span>
                          <strong>{userStats.createdThisMonth}</strong> this month
                        </span>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <FormContainer>
                <ExpandedHeader style={{ borderBottomColor: 'rgba(255,255,255,0.15)' }}>
                  <ExpandedTitle style={{ color: 'white' }}>
                    <SparklesIcon style={{ color: '#4285F4' }} />
                    Plan Your Journey
                  </ExpandedTitle>
                </ExpandedHeader>

              <form onSubmit={handleSubmit}>
                {/* Sentence Builder: "I want to go to [dest] for [days] days with a [budget] budget" */}
                <SentenceBuilder>
                  <SentenceText>I want to go to</SentenceText>
                  <div style={{ minWidth: '160px', maxWidth: '260px', flex: '1 1 160px' }}>
                    <PlaceAutocomplete
                      value={formData.place}
                      onChange={(value) => setFormData({ ...formData, place: value })}
                      onSelect={(place) => {
                        setFormData({ ...formData, place: place.name });
                      }}
                      placeholder="destination..."
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                      glassTheme
                    />
                  </div>
                  <SentenceText>for</SentenceText>
                  <SentenceInput
                    type="number"
                    name="days"
                    min="1"
                    max="10"
                    placeholder="3"
                    $width="50px"
                    value={formData.days || ''}
                    onChange={handleInputChange}
                    required
                    title="Maximum 10 days allowed"
                  />
                  <SentenceText>days with a</SentenceText>
                  <SentenceSelect
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                  >
                    {BUDGET_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </SentenceSelect>
                  <SentenceText>budget</SentenceText>
                </SentenceBuilder>

                {/* Travel Style Cards */}
                <GlassFieldLabel>Travel Style</GlassFieldLabel>
                <TravelStyleGrid>
                  {TRAVEL_STYLE_OPTIONS.map((style) => (
                    <TravelStyleCard
                      key={style.value}
                      type="button"
                      $active={formData.itineraryType === style.value}
                      onClick={() => setFormData({ ...formData, itineraryType: style.value })}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <TravelStyleImage $src={style.image} />
                      <TravelStyleLabel>
                        <div className="title">{style.label}</div>
                        <div className="desc">{style.description}</div>
                      </TravelStyleLabel>
                    </TravelStyleCard>
                  ))}
                </TravelStyleGrid>

                {/* Or pick from full list */}
                <div style={{ marginBottom: '1rem' }}>
                  <GlassFieldLabel>Or choose a specific type</GlassFieldLabel>
                  <GlassSelect
                    name="itineraryType"
                    value={formData.itineraryType}
                    onChange={handleInputChange}
                  >
                    {ITINERARY_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </GlassSelect>
                </div>

                {/* Magic Box - Traveler Profile */}
                <GlassFieldLabel>Tell us about yourself (optional)</GlassFieldLabel>
                <MagicBoxTextarea
                  name="travelerProfile"
                  placeholder="e.g., family with toddlers who love street food, early risers, need wheelchair access..."
                  value={formData.travelerProfile}
                  onChange={handleInputChange}
                  maxLength={TRAVELER_PROFILE_MAX}
                  spellCheck
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  {SQL_INJECTION_PATTERN.test(formData.travelerProfile || '') ? (
                    <span style={{ fontSize: '11px', color: '#f87171', fontWeight: 600 }}>
                      ⚠ Invalid characters detected — please describe your travel preferences in plain text
                    </span>
                  ) : (
                    <span />
                  )}
                  <span style={{ fontSize: '11px', color: (formData.travelerProfile || '').length >= TRAVELER_PROFILE_MAX ? '#f87171' : 'rgba(255,255,255,0.4)', marginLeft: 'auto' }}>
                    {(formData.travelerProfile || '').length}/{TRAVELER_PROFILE_MAX}
                  </span>
                </div>

                {/* Advanced Options Toggle */}
                <GlassOptionsToggle
                  type="button"
                  $isOpen={showAdvancedOptions}
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  whileTap={{ scale: 0.98 }}
                >
                  {showAdvancedOptions ? 'Less options' : 'More options'}
                  <ChevronRightIcon style={{ transform: showAdvancedOptions ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                </GlassOptionsToggle>

                {/* Advanced Options Grid */}
                {showAdvancedOptions && (
                    <GlassOptionsGrid>
                      <FieldGroup>
                        <GlassFieldLabel>Month</GlassFieldLabel>
                        <GlassSelect
                          name="month"
                          value={formData.month}
                          onChange={handleInputChange}
                        >
                          {MONTH_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </GlassSelect>
                      </FieldGroup>

                      <FieldGroup>
                        <GlassFieldLabel>Transportation</GlassFieldLabel>
                        <GlassSelect
                          name="transportation"
                          value={formData.transportation}
                          onChange={handleInputChange}
                        >
                          {TRANSPORTATION_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </GlassSelect>
                      </FieldGroup>

                      <FieldGroup>
                        <GlassFieldLabel>Arrival</GlassFieldLabel>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {['morning', 'evening'].map(val => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, arrivalTime: val }))}
                              style={{
                                flex: 1,
                                padding: '0.75rem',
                                background: formData.arrivalTime === val ? 'rgba(66, 133, 244, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                                border: formData.arrivalTime === val ? '1px solid rgba(66, 133, 244, 0.6)' : '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '0.9375rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {val === 'morning' ? 'Morning' : 'Evening'}
                            </button>
                          ))}
                        </div>
                      </FieldGroup>
                    </GlassOptionsGrid>
                )}

                {limitStatus && !limitStatus.canCreate && (
                  <ErrorState style={{ marginBottom: DesignTokens.spacing.md, background: 'rgba(239, 68, 68, 0.2)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <XCircleIcon style={{ width: '20px', height: '20px' }} />
                    <div>
                      <strong>Limit Reached</strong>
                      <div style={{ fontSize: DesignTokens.typography.fontSize.sm, marginTop: DesignTokens.spacing.xs }}>
                        {limitStatus.message || 'You\'ve reached the limit of 3 itineraries per 24 hours. Please try again later.'}
                      </div>
                    </div>
                  </ErrorState>
                )}
                {limitStatus && limitStatus.canCreate && limitStatus.count > 0 && (
                  <div style={{
                    marginBottom: DesignTokens.spacing.md,
                    padding: DesignTokens.spacing.sm,
                    background: 'rgba(66, 133, 244, 0.15)',
                    borderRadius: DesignTokens.borderRadius.md,
                    fontSize: DesignTokens.typography.fontSize.sm,
                    color: 'rgba(255,255,255,0.8)',
                    border: '1px solid rgba(66, 133, 244, 0.3)'
                  }}>
                    {limitStatus.message || `You can create ${3 - limitStatus.count} more itinerary(ies) today.`}
                  </div>
                )}
                <GenerateButton
                  type="submit"
                  disabled={
                    itineraryMutation.isLoading ||
                    !formData.place ||
                    formData.place.trim() === '' ||
                    (limitStatus && !limitStatus.canCreate)
                  }
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {itineraryMutation.isLoading ? (
                    <>
                      <SparklesIcon style={{ animation: 'spin 1s linear infinite' }} />
                      <span>Creating your perfect itinerary...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon />
                      <span>Generate {formData.days || 3}-Day Itinerary</span>
                    </>
                  )}
                </GenerateButton>
                <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                  <motion.button
                    type="button"
                    onClick={() => setShowPreferencesModal(true)}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      padding: '6px 16px',
                      background: 'none',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Customize Preferences
                  </motion.button>
                </div>
              </form>
            </FormContainer>
            </>
          )}

          {/* Show "No Itineraries" message for authenticated users with no itineraries */}
          {isAuthenticated && viewMode === 'myItineraries' && (!createdItineraries || createdItineraries.length === 0) && showNoItinerariesMessage && !resolvedId && !itineraryData && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  padding: DesignTokens.spacing.lg,
                  background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                  borderRadius: DesignTokens.borderRadius.md,
                  marginBottom: DesignTokens.spacing.lg,
                  border: '2px solid #93C5FD',
                  position: 'relative',
                  textAlign: 'center'
                }}
              >
                {/* Close button */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowNoItinerariesMessage(false)}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1E40AF',
                    opacity: 0.6
                  }}
                >
                  <XCircleIcon style={{ width: '20px', height: '20px' }} />
                </motion.button>

                {/* Icon */}
                <div style={{ marginBottom: '12px' }}>
                  <MapPinIcon style={{ width: '48px', height: '48px', color: '#3B82F6', margin: '0 auto' }} />
                </div>

                {/* Title */}
                <div style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1E40AF',
                  marginBottom: '8px'
                }}>
                  No Itineraries Yet
                </div>

                {/* Message */}
                <div style={{
                  fontSize: '14px',
                  color: '#1E3A8A',
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  You haven't created any itineraries yet. Start planning your next adventure!
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setViewMode('default');
                    setShowNoItinerariesMessage(false);
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#3B82F6',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <SparklesIcon style={{ width: '18px', height: '18px' }} />
                  Create Your First Itinerary
                </motion.button>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Show User's Created Itineraries - Show below form in default mode, or as main content in myItineraries mode */}
          {createdItineraries && createdItineraries.length > 0 && !resolvedId && !itineraryData && (
            <AvailableItinerariesContainer style={{ marginBottom: DesignTokens.spacing.xxl }}>
              <SectionHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <SectionTitle>
                      <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                      My Itineraries ({createdItineraries?.length || 0})
                    </SectionTitle>
                    <SectionSubtitle>Itineraries you've created</SectionSubtitle>
                  </div>
                  {viewMode === 'default' && createdItineraries.length >= 3 && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setViewMode('myItineraries')}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'rgb(215 147 251)',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      View All
                      <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
                    </motion.button>
                  )}
                  {viewMode === 'myItineraries' && !isMobile && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setViewMode('default')}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'rgb(215 147 251)',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <SparklesIcon style={{ width: '16px', height: '16px' }} />
                      Create New Itinerary
                    </motion.button>
                  )}
                </div>
              </SectionHeader>

              <ItineraryCardsContainer>
                {(viewMode === 'myItineraries' ? createdItineraries : createdItineraries.slice(0, 3)).map((itinerary, index) => {
                  const isRecent = isRecentlyCreated(itinerary.createdAt);
                  const relativeTime = formatRelativeTime(itinerary.createdAt);
                  const itineraryUrl = itinerary.slug || itinerary.id;
                  const tagline = generateItineraryTagline(itinerary);

                  return (
                    <ItineraryCard
                      key={`created-${itinerary.id}`}
                      onClick={() => navigate(`/itinerary/${itineraryUrl}`)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -4 }}
                    >
                      <ItineraryImage imageUrl={itinerary.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (index * 1000)}?w=800&q=80&auto=format&fit=crop`}>
                        {isRecent && (
                          <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            style={{
                              position: 'absolute',
                              top: '12px',
                              left: '12px',
                              padding: '6px 12px',
                              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                              color: 'white',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '700',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            ✨ New
                          </motion.div>
                        )}
                        {itinerary.isFeatured && (
                          <AIBadgeSmall>Featured</AIBadgeSmall>
                        )}
                      </ItineraryImage>
                      <ItineraryContent>
                        <ItineraryCardTitle>{tagline}</ItineraryCardTitle>
                        <ItineraryCardDescription>
                          {itinerary.description || itinerary.tripSummary || 'Explore this destination'}
                        </ItineraryCardDescription>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                          Created {relativeTime}
                        </div>
                        <ItineraryCardMeta style={{ marginTop: DesignTokens.spacing.sm }}>
                          <div style={{ display: 'flex', gap: DesignTokens.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <CalendarDaysIcon style={{ width: '14px', height: '14px' }} />
                              {itinerary.durationDays || itinerary.days || 0} days
                            </span>
                            {itinerary.budgetRange && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <CurrencyDollarIcon style={{ width: '14px', height: '14px' }} />
                                {itinerary.budgetRange}
                              </span>
                            )}
                            {itinerary.itineraryType && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: '#F3E8FF',
                                color: '#7C3AED',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}>
                                {itinerary.itineraryType}
                              </span>
                            )}
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!userId) {
                                toast.info('Please refresh the page to enable favorites');
                                return;
                              }
                              mainFavoriteMutation.mutate({ itineraryId: itinerary.id });
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {itinerary.isFavoritedByUser ? (
                              <HeartIconSolid style={{ width: '20px', height: '20px', color: '#EF4444' }} />
                            ) : (
                              <HeartIcon style={{ width: '20px', height: '20px', color: '#9CA3AF' }} />
                            )}
                          </motion.button>
                        </ItineraryCardMeta>
                      </ItineraryContent>
                    </ItineraryCard>
                  );
                })}
              </ItineraryCardsContainer>
            </AvailableItinerariesContainer>
          )}

          {/* Browse All Itineraries Button - Only in default mode */}
          {!showBrowseAll && !selectedCountryFilter && viewMode === 'default' && (
            <AvailableItinerariesContainer>
              <SectionHeader>
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <BrowseAllButton
                    onClick={() => {
                      setShowBrowseAll(true);
                      refetchAllItineraries();
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SparklesIcon style={{ width: '20px', height: '20px' }} />
                    Browse All Itineraries
                  </BrowseAllButton>
                </div>
              </SectionHeader>
            </AvailableItinerariesContainer>
          )}
          
          {/* Show ALL Itineraries when "Browse All" is clicked */}
          {showBrowseAll && !selectedCountryFilter && (
            <AvailableItinerariesContainer>
              <SectionHeader>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <SectionTitle>
                      <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                      All Available Itineraries
                    </SectionTitle>
                    <SectionSubtitle>
                      {isLoadingAllItineraries ? 'Loading...' : `Showing ${allItineraries?.length || 0} itineraries`}
                    </SectionSubtitle>
                  </div>
                  <ToggleViewButton
                    onClick={() => setShowBrowseAll(false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Show By Country
                  </ToggleViewButton>
                </div>
              </SectionHeader>
              
              {isLoadingAllItineraries ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Loading all itineraries...</div>
              ) : allItineraries && allItineraries.length > 0 ? (
                <ItineraryCardsContainer>
                  {allItineraries.map((itinerary, index) => {
                    const tagline = generateItineraryTagline(itinerary);
                    const isFavorited = itinerary.isFavoritedByUser || false;
                    const itineraryUrl = itinerary.slug || itinerary.id;
                    
                    return (
                      <ItineraryCard
                        key={`all-${itinerary.id}`}
                        onClick={() => navigate(`/itinerary/${itineraryUrl}`)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -4 }}
                      >
                        <ItineraryImage imageUrl={itinerary.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (index * 1000)}?w=800&q=80&auto=format&fit=crop`}>
                          <AIBadgeSmall style={{ background: isFavorited ? '#EF4444' : undefined }}>
                            {isFavorited ? (
                              <>
                                <HeartIconSolid style={{ width: '12px', height: '12px' }} />
                                Saved
                              </>
                            ) : (
                              <>
                                <SparklesIcon style={{ width: '12px', height: '12px' }} />
                                ✨ Explore
                              </>
                            )}
                          </AIBadgeSmall>
                        </ItineraryImage>
                        <ItineraryContent>
                          <ItineraryCardTitle>{tagline}</ItineraryCardTitle>
                          <ItineraryCardDescription>
                            {itinerary.description || itinerary.tripSummary || `Explore ${itinerary.country}.`}
                          </ItineraryCardDescription>
                          <ItineraryCardMeta>
                            <CalendarDaysIcon style={{ width: '14px', height: '14px' }} />
                            {itinerary.durationDays || itinerary.days || 0} days
                            {' • '}
                            <CurrencyDollarIcon style={{ width: '14px', height: '14px' }} />
                            {itinerary.budgetRange || 'mid-range'}
                            {itinerary.updatedAt && (
                              <>
                                {' • '}
                                <ClockIcon style={{ width: '14px', height: '14px' }} />
                                {new Date(itinerary.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </>
                            )}
                          </ItineraryCardMeta>
                        </ItineraryContent>
                      </ItineraryCard>
                    );
                  })}
                </ItineraryCardsContainer>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center' }}>No itineraries available yet.</div>
              )}
            </AvailableItinerariesContainer>
          )}

          {/* Show Available Itineraries (matching Featured Itineraries style) */}
          {!showBrowseAll && (isLoadingFeatured || isLoadingCountries ? (
            <AvailableItinerariesContainer>
              <SectionHeader>
                <SectionTitle>
                  <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                  Featured Itineraries
                </SectionTitle>
                <SectionSubtitle>Loading available itineraries...</SectionSubtitle>
              </SectionHeader>
            </AvailableItinerariesContainer>
          ) : (() => {
            // Group itineraries by country and get counts
            const countryMap = new Map();
            
            // Add countries from featured itineraries
            if (featuredItineraries && featuredItineraries.length > 0) {
              featuredItineraries.forEach(itinerary => {
                if (itinerary.country) {
                  if (!countryMap.has(itinerary.country)) {
                    countryMap.set(itinerary.country, []);
                  }
                  countryMap.get(itinerary.country).push(itinerary);
                }
              });
            }
            
            // Merge with countriesWithCounts for accurate counts
            const countriesWithItineraries = [];
            if (countriesWithCounts && countriesWithCounts.length > 0) {
              countriesWithCounts.forEach(countryInfo => {
                const country = countryInfo.country;
                const count = countryInfo.count || 0;
                const itineraries = countryMap.get(country) || [];
                countriesWithItineraries.push({
                  country,
                  count,
                  itineraries,
                  hasItineraries: itineraries.length > 0
                });
              });
            } else {
              // Fallback: use countries from featured itineraries
              countryMap.forEach((itineraries, country) => {
                countriesWithItineraries.push({
                  country,
                  count: itineraries.length,
                  itineraries,
                  hasItineraries: true
                });
              });
            }
            
            // Filter by selected country if filter is active
            let filteredCountries = countriesWithItineraries;
            if (selectedCountryFilter) {
              filteredCountries = countriesWithItineraries.filter(
                countryData => countryData.country === selectedCountryFilter
              );
            }
            
            // Sort by count (descending) - show all countries
            filteredCountries.sort((a, b) => b.count - a.count);
            const topCountries = filteredCountries; // Show all countries, not just top 6
            
            // If filter is active, show individual itineraries for that country
            if (selectedCountryFilter && topCountries.length > 0) {
              const selectedCountryData = topCountries[0];
              const countryItineraries = selectedCountryData.itineraries || [];
              
              return countryItineraries.length > 0 ? (
                <AvailableItinerariesContainer>
                  <SectionHeader>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <div>
                        <SectionTitle>
                          <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                          Itineraries for {selectedCountryFilter}
                        </SectionTitle>
                        <SectionSubtitle>
                          {countryItineraries.length} {countryItineraries.length === 1 ? 'itinerary' : 'itineraries'} available
                        </SectionSubtitle>
                      </div>
                      <motion.button
                        onClick={() => setSelectedCountryFilter(null)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          padding: `${DesignTokens.spacing.sm} ${DesignTokens.spacing.md}`,
                          background: DesignTokens.colors?.primaryLight || '#E8F0FE',
                          color: DesignTokens.colors?.primary || '#4285F4',
                          border: 'none',
                          borderRadius: DesignTokens.borderRadius.md,
                          cursor: 'pointer',
                          fontSize: DesignTokens.typography.fontSize.sm,
                          fontWeight: DesignTokens.typography.fontWeight.medium
                        }}
                      >
                        Show All Countries
                      </motion.button>
                    </div>
                  </SectionHeader>
                  <ItineraryCardsContainer>
                    {countryItineraries.map((itinerary, index) => {
                      const tagline = generateItineraryTagline(itinerary);
                      return (
                        <ItineraryCard
                          key={itinerary.id || index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -4 }}
                          onClick={() => {
                            const url = itinerary.slug || itinerary.id;
                            navigate(`/itinerary/${url}`, { replace: true });
                          }}
                        >
                          <ItineraryImage imageUrl={itinerary.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (index * 1000)}?w=800&q=80&auto=format&fit=crop`}>
                            {itinerary.itineraryType && (
                              <AIBadgeSmall>
                                <SparklesIcon style={{ width: '12px', height: '12px' }} />
                                {itinerary.itineraryType === 'culture' || itinerary.itineraryType === 'cultural' ? 'Cultural' :
                                 itinerary.itineraryType === 'culinary' ? 'Culinary' :
                                 itinerary.itineraryType === 'gastronomy' ? 'Gastronomy' :
                                 itinerary.itineraryType === 'romantic' ? 'Romantic' :
                                 itinerary.itineraryType === 'adventure' ? 'Adventure' :
                                 itinerary.itineraryType === 'party' ? 'Party' :
                                 itinerary.itineraryType === 'peace' ? 'Peaceful' :
                                 itinerary.itineraryType === 'relax' ? 'Relax' :
                                 itinerary.itineraryType === 'family' ? 'Family' :
                                 itinerary.itineraryType === 'solo' ? 'Solo' :
                                 itinerary.itineraryType === 'nature' ? 'Nature' :
                                 itinerary.itineraryType === 'historical' ? 'Historical' :
                                 itinerary.itineraryType === 'beach' ? 'Beach' :
                                 itinerary.itineraryType === 'mountain' ? 'Mountain' :
                                 itinerary.itineraryType.charAt(0).toUpperCase() + itinerary.itineraryType.slice(1)}
                              </AIBadgeSmall>
                            )}
                          </ItineraryImage>
                          <ItineraryContent>
                            <ItineraryCardTitle>{tagline}</ItineraryCardTitle>
                            <ItineraryCardDescription>
                              {itinerary.description || itinerary.tripSummary || `Explore ${itinerary.country || 'your destination'} with this curated itinerary.`}
                            </ItineraryCardDescription>
                            <ItineraryCardMeta>
                              {itinerary.durationDays || itinerary.days || 0} days • Click to view →
                            </ItineraryCardMeta>
                          </ItineraryContent>
                        </ItineraryCard>
                      );
                    })}
                  </ItineraryCardsContainer>
                </AvailableItinerariesContainer>
              ) : (
                <AvailableItinerariesContainer>
                  <SectionHeader>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <div>
                        <SectionTitle>
                          <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                          Itineraries for {selectedCountryFilter}
                        </SectionTitle>
                        <SectionSubtitle>No itineraries available yet. Create your first itinerary below!</SectionSubtitle>
                      </div>
                      <motion.button
                        onClick={() => setSelectedCountryFilter(null)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          padding: `${DesignTokens.spacing.sm} ${DesignTokens.spacing.md}`,
                          background: DesignTokens.colors?.primaryLight || '#E8F0FE',
                          color: DesignTokens.colors?.primary || '#4285F4',
                          border: 'none',
                          borderRadius: DesignTokens.borderRadius.md,
                          cursor: 'pointer',
                          fontSize: DesignTokens.typography.fontSize.sm,
                          fontWeight: DesignTokens.typography.fontWeight.medium
                        }}
                      >
                        Show All Countries
                      </motion.button>
                    </div>
                  </SectionHeader>
                </AvailableItinerariesContainer>
              );
            }
            
            return topCountries.length > 0 ? (
              <AvailableItinerariesContainer>
                <SectionHeader>
                  <SectionTitle>
                    <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                    Featured Itineraries
                  </SectionTitle>
                  <SectionSubtitle>
                    Discover curated travel plans and explore heritage sites and famous destinations around the world
                  </SectionSubtitle>
                </SectionHeader>
                
                {/* Countries Carousel */}
                <ItineraryCarouselContainer>
                  <CarouselPrevButton
                    onClick={() => {
                      const container = countriesCarouselRef.current;
                      if (container) {
                        container.scrollBy({ left: -340, behavior: 'smooth' });
                      }
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Previous countries"
                  >
                    <ChevronLeftIcon />
                  </CarouselPrevButton>
                  
                  <ItineraryCarouselCardsContainer ref={countriesCarouselRef}>
                    {topCountries.map((countryData, countryIndex) => {
                      const country = countryData.country;
                      const count = countryData.count;
                      const countryImage = `https://images.unsplash.com/photo-${1500000000000 + (countryIndex * 1000)}?w=800&q=80&auto=format&fit=crop`;
                      const isExpanded = expandedCountry === country;
                      
                      return (
                        <CountryCard
                          key={country}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: countryIndex * 0.05 }}
                          whileHover={{ y: -4 }}
                          onClick={() => {
                            setExpandedCountry(isExpanded ? null : country);
                          }}
                        >
                          <ItineraryImage imageUrl={countryImage}>
                            <AIBadgeSmall>
                              <SparklesIcon style={{ width: '12px', height: '12px' }} />
                              {count} {count === 1 ? 'Itinerary' : 'Itineraries'}
                            </AIBadgeSmall>
                          </ItineraryImage>
                          <ItineraryContent>
                            <ItineraryCardTitle>
                              {country}
                              {isExpanded && <ArrowRightIcon style={{ width: '16px', height: '16px', display: 'inline', marginLeft: '8px', transform: 'rotate(90deg)' }} />}
                            </ItineraryCardTitle>
                            <ItineraryCardDescription>
                              {isExpanded ? 'Click to collapse' : `Click to view ${count} ${count === 1 ? 'itinerary' : 'itineraries'}`}
                            </ItineraryCardDescription>
                            <ItineraryCardMeta>
                              <MapPinIcon style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px' }} />
                              {count} {count === 1 ? 'itinerary' : 'itineraries'} available
                            </ItineraryCardMeta>
                          </ItineraryContent>
                        </CountryCard>
                      );
                    })}
                  </ItineraryCarouselCardsContainer>
                  
                  <CarouselNextButton
                    onClick={() => {
                      const container = countriesCarouselRef.current;
                      if (container) {
                        container.scrollBy({ left: 340, behavior: 'smooth' });
                      }
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Next countries"
                  >
                    <ChevronRightIcon />
                  </CarouselNextButton>
                </ItineraryCarouselContainer>
                
                {/* Show itineraries carousel for expanded country */}
                {expandedCountry && (
                  <CountryItinerariesCarousel
                    country={expandedCountry}
                    countryIndex={topCountries.findIndex(c => c.country === expandedCountry)}
                    carouselRefs={carouselRefs}
                    setSearchParams={setSearchParams}
                    navigate={navigate}
                    setSelectedCountryFilter={setSelectedCountryFilter}
                    setFormData={setFormData}
                    generateItineraryTagline={generateItineraryTagline}
                    onClose={() => setExpandedCountry(null)}
                  />
                )}
              </AvailableItinerariesContainer>
            ) : (
              <AvailableItinerariesContainer>
                <SectionHeader>
                  <SectionTitle>
                    <SparklesIcon style={{ width: '28px', height: '28px', color: 'rgb(215 147 251)' }} />
                    Featured Itineraries
                  </SectionTitle>
                  <SectionSubtitle>No itineraries available yet. Create your first itinerary below!</SectionSubtitle>
                </SectionHeader>
              </AvailableItinerariesContainer>
            );
          })())}

          {/* Divider between available itineraries and form */}
          {featuredItineraries && featuredItineraries.length > 0 && (
            <div style={{
              margin: `${DesignTokens.spacing['2xl']} 0`,
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                height: '1px',
                background: `linear-gradient(to right, transparent, ${DesignTokens.colors?.border || '#E0E0E0'}, transparent)`,
                margin: `${DesignTokens.spacing.xl} 0`
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: DesignTokens.colors?.background || '#FFFFFF',
                padding: `0 ${DesignTokens.spacing.lg}`,
                color: DesignTokens.colors?.textSecondary || '#666',
                fontSize: DesignTokens.typography.fontSize.sm,
                fontWeight: DesignTokens.typography.fontWeight.medium
              }}>
                Or Create a New Itinerary
              </div>
            </div>
          )}

          {/* Show existing itineraries if available (only if not already shown in featured carousel or expanded country) */}
          {!resolvedId && !itineraryData && existingItineraries && existingItineraries.length > 0 && 
           !expandedCountry && 
           formData.place && formData.place.trim() !== '' &&
           (!featuredItineraries || featuredItineraries.length === 0 || 
            !featuredItineraries.some(it => it.country && it.country.toLowerCase() === formData.place?.toLowerCase())) && (
            <div style={{
              marginBottom: DesignTokens.spacing.xl,
              padding: DesignTokens.spacing.lg,
              background: DesignTokens.colors?.surface || '#F8F9FA',
              borderRadius: DesignTokens.borderRadius.md
            }}>
              <h3 style={{
                fontSize: DesignTokens.typography.fontSize.lg,
                fontWeight: DesignTokens.typography.fontWeight.semibold,
                marginBottom: DesignTokens.spacing.md,
                color: DesignTokens.colors?.text || '#333'
              }}>
                Existing Itineraries for {formData.place}
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: DesignTokens.spacing.md
              }}>
                {existingItineraries.map((itinerary) => (
                  <motion.div
                    key={itinerary.id}
                    onClick={() => {
                      const url = itinerary.slug || itinerary.id;
                      navigate(`/itinerary/${url}`, { replace: true });
                    }}
                    style={{
                      padding: DesignTokens.spacing.md,
                      background: DesignTokens.colors?.background || '#FFFFFF',
                      borderRadius: DesignTokens.borderRadius.md,
                      border: `1px solid ${DesignTokens.colors?.border || '#E0E0E0'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: DesignTokens.spacing.sm,
                      marginBottom: DesignTokens.spacing.xs,
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: DesignTokens.spacing.sm,
                        flex: 1,
                        minWidth: 0
                      }}>
                        <SparklesIcon style={{ width: '20px', height: '20px', color: 'rgb(215 147 251)' }} />
                        <strong style={{ fontSize: DesignTokens.typography.fontSize.base }}>
                          {itinerary.title || generateItineraryTagline(itinerary)}
                        </strong>
                      </div>
                      {itinerary.itineraryType && (
                        <ItineraryTypeBadge style={{ marginLeft: 'auto', flexShrink: 0 }}>
                          <SparklesIcon style={{ width: '10px', height: '10px' }} />
                          {itinerary.itineraryType === 'culture' || itinerary.itineraryType === 'cultural' ? 'Cultural' :
                           itinerary.itineraryType === 'culinary' ? 'Culinary' :
                           itinerary.itineraryType === 'gastronomy' ? 'Gastronomy' :
                           itinerary.itineraryType === 'romantic' ? 'Romantic' :
                           itinerary.itineraryType === 'adventure' ? 'Adventure' :
                           itinerary.itineraryType === 'party' ? 'Party' :
                           itinerary.itineraryType === 'peace' ? 'Peaceful' :
                           itinerary.itineraryType === 'relax' ? 'Relax' :
                           itinerary.itineraryType === 'family' ? 'Family' :
                           itinerary.itineraryType === 'solo' ? 'Solo' :
                           itinerary.itineraryType === 'nature' ? 'Nature' :
                           itinerary.itineraryType === 'historical' ? 'Historical' :
                           itinerary.itineraryType === 'beach' ? 'Beach' :
                           itinerary.itineraryType === 'mountain' ? 'Mountain' :
                           itinerary.itineraryType.charAt(0).toUpperCase() + itinerary.itineraryType.slice(1)}
                        </ItineraryTypeBadge>
                      )}
                    </div>
                    <div style={{
                      fontSize: DesignTokens.typography.fontSize.sm,
                      color: DesignTokens.colors?.textSecondary || '#666',
                      marginBottom: DesignTokens.spacing.xs,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: DesignTokens.spacing.xs,
                      alignItems: 'center'
                    }}>
                      <span>{itinerary.country} • {itinerary.durationDays || itinerary.days || 0} days</span>
                      {itinerary.updatedAt && (
                        <>
                          <span>•</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ClockIcon style={{ width: '14px', height: '14px' }} />
                            Updated {new Date(itinerary.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </>
                      )}
                      {/* Read-only favorite indicator */}
                      {(itinerary.isFavoritedByUser || (itinerary.favoriteCount > 0)) && (
                        <>
                          <span>•</span>
                          <span style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            color: itinerary.isFavoritedByUser ? '#EF4444' : DesignTokens.colors?.textTertiary || '#666'
                          }}>
                            {itinerary.isFavoritedByUser ? (
                              <HeartIconSolid style={{ width: '14px', height: '14px' }} />
                            ) : (
                              <HeartIcon style={{ width: '14px', height: '14px' }} />
                            )}
                            {itinerary.favoriteCount > 0 && (
                              <span>{itinerary.favoriteCount}</span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    {itinerary.description && (
                      <div style={{
                        fontSize: DesignTokens.typography.fontSize.sm,
                        color: DesignTokens.colors?.text || '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {itinerary.description}
                      </div>
                    )}
                    <div style={{
                      marginTop: DesignTokens.spacing.sm,
                      fontSize: DesignTokens.typography.fontSize.xs,
                      color: DesignTokens.colors?.primary || '#4285F4',
                      fontWeight: DesignTokens.typography.fontWeight.medium
                    }}>
                      Click to view →
                    </div>
                  </motion.div>
                ))}
              </div>
              <div style={{
                marginTop: DesignTokens.spacing.md,
                padding: DesignTokens.spacing.sm,
                background: DesignTokens.colors?.secondaryLight || '#E6F4EA',
                borderRadius: DesignTokens.borderRadius.sm,
                fontSize: DesignTokens.typography.fontSize.sm,
                color: DesignTokens.colors?.text || '#333',
                textAlign: 'center'
              }}>
                Or generate a new itinerary below
              </div>
            </div>
          )}

          {/* Show Saved Itineraries if user has any - Only in default mode */}
          {savedItineraries && savedItineraries.length > 0 && viewMode === 'default' && (
            <AvailableItinerariesContainer>
              <SectionHeader>
                <SectionTitle>
                  <HeartIconSolid style={{ width: '28px', height: '28px', color: '#EF4444' }} />
                  My Saved Itineraries ({savedItineraries.length})
                </SectionTitle>
                <SectionSubtitle>Your favorited travel plans</SectionSubtitle>
              </SectionHeader>
              <ItineraryCardsContainer>
                {savedItineraries.map((itinerary, index) => {
                  const tagline = generateItineraryTagline(itinerary);
                  return (
                    <ItineraryCard
                      key={`saved-${itinerary.id}`}
                      onClick={() => {
                        navigate(`/itinerary/${itinerary.slug || itinerary.id}`);
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -4 }}
                    >
                      <ItineraryImage imageUrl={itinerary.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (index * 1000)}?w=800&q=80&auto=format&fit=crop`}>
                        <AIBadgeSmall style={{ background: '#EF4444' }}>
                          <HeartIconSolid style={{ width: '12px', height: '12px' }} />
                          Saved
                        </AIBadgeSmall>
                      </ItineraryImage>
                      <ItineraryContent>
                        <ItineraryCardTitle>{tagline}</ItineraryCardTitle>
                        <ItineraryCardDescription>
                          {itinerary.description || itinerary.tripSummary || `Explore ${itinerary.country} with this saved itinerary.`}
                        </ItineraryCardDescription>
                        <ItineraryCardMeta>
                          <CalendarDaysIcon style={{ width: '14px', height: '14px' }} />
                          {itinerary.durationDays || itinerary.days || 0} days
                          {' • '}
                          <CurrencyDollarIcon style={{ width: '14px', height: '14px' }} />
                          {itinerary.budgetRange || 'mid-range'}
                          {itinerary.createdAt && (
                            <>
                              {' • '}
                              <ClockIcon style={{ width: '14px', height: '14px' }} />
                              Saved {new Date(itinerary.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </>
                          )}
                        </ItineraryCardMeta>
                      </ItineraryContent>
                    </ItineraryCard>
                  );
                })}
              </ItineraryCardsContainer>
            </AvailableItinerariesContainer>
          )}


          {/* Show Saved Itineraries FIRST if user has any - Only in default mode */}
          {savedItineraries && savedItineraries.length > 0 && viewMode === 'default' && (
            <AvailableItinerariesContainer>
                <SectionHeader>
                  <SectionTitle>
                    <HeartIconSolid style={{ width: '28px', height: '28px', color: '#EF4444' }} />
                    My Saved Itineraries ({savedItineraries.length})
                  </SectionTitle>
                  <SectionSubtitle>Your favorited travel plans</SectionSubtitle>
                </SectionHeader>
                <ItineraryCardsContainer>
                  {savedItineraries.map((itinerary, index) => {
                    const tagline = generateItineraryTagline(itinerary);
                    return (
                      <ItineraryCard
                        key={`saved-${itinerary.id}`}
                        onClick={() => {
                          navigate(`/itinerary/${itinerary.slug || itinerary.id}`);
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -4 }}
                      >
                        <ItineraryImage imageUrl={itinerary.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (index * 1000)}?w=800&q=80&auto=format&fit=crop`}>
                          <AIBadgeSmall style={{ background: '#EF4444' }}>
                            <HeartIconSolid style={{ width: '12px', height: '12px' }} />
                            Saved
                          </AIBadgeSmall>
                        </ItineraryImage>
                        <ItineraryContent>
                          <ItineraryCardTitle>{tagline}</ItineraryCardTitle>
                          <ItineraryCardDescription>
                            {itinerary.description || itinerary.tripSummary || `Explore ${itinerary.country} with this saved itinerary.`}
                          </ItineraryCardDescription>
                          <ItineraryCardMeta>
                            <CalendarDaysIcon style={{ width: '14px', height: '14px' }} />
                            {itinerary.durationDays || itinerary.days || 0} days
                            {' • '}
                            <CurrencyDollarIcon style={{ width: '14px', height: '14px' }} />
                            {itinerary.budgetRange || 'mid-range'}
                            {itinerary.createdAt && (
                              <>
                                {' • '}
                                <ClockIcon style={{ width: '14px', height: '14px' }} />
                                Saved {new Date(itinerary.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </>
                            )}
                          </ItineraryCardMeta>
                        </ItineraryContent>
                      </ItineraryCard>
                    );
                  })}
                </ItineraryCardsContainer>
              </AvailableItinerariesContainer>
            )}

          </ScenicFormBackground>
          </>
        ) : (itineraryData || (resolvedId && fetchedItinerary)) ? (
          // Show itinerary with day cards - use itineraryData or fetchedItinerary
          (() => {
            // Use itineraryData from state if available, otherwise use fetchedItinerary from API
            // This ensures the page shows content immediately on reload
            const displayData = itineraryData || fetchedItinerary;

            return (
              <div ref={itineraryContentRef}>

            {/* Full-bleed Split View: Day Timeline + Map — scenic background */}
            <ItinerarySplitView
              itineraryData={displayData}
              mapRef={mapRef}
              userId={userId}
              isAuthenticated={isAuthenticated}
              fetchedItinerary={fetchedItinerary}
              onActivityClick={(activity, dayIndex, activityIndex) => {
                // Activity click handler - can be used for detail modal
              }}
              onBookAll={() => setShowBudgetPlanner(true)}
              extractedCity={extractedCity}
              onCityExtracted={(city) => setExtractedCity(city)}
            />

            {/* Trip details & tips are now shown as floating info cards on the map */}
          </div>
            );
          })()
        ) : (
          // Show loading/error states
          <Container>
            {/* Loading State - Fetching Itinerary by ID */}
            {isLoadingItinerary && (
              <LoadingState>
                <SparklesIcon style={{ width: '48px', height: '48px', color: 'rgb(215 147 251)', animation: 'spin 1s linear infinite' }} />
                <div>Loading itinerary...</div>
              </LoadingState>
            )}

            {/* Error State - Fetching Itinerary by ID */}
            {itineraryError && (
              <ErrorState>
                <XCircleIcon style={{ width: '24px', height: '24px' }} />
                {apiUtils.handleError(itineraryError).message || 'Failed to load itinerary. Please try again.'}
              </ErrorState>
            )}

            {/* Loading State - Generating Itinerary with Compass Loader */}
            {itineraryMutation.isLoading && (
              <LoaderContainer>
                <LoaderDialog>
                  <LoaderSpinner />
                  <LoaderText>
                    <LoaderMainText>Generating your Gold Standard Itinerary...</LoaderMainText>
                    <LoaderSubText>Discovering hidden gems...</LoaderSubText>
                    <LoaderSubText>Adding your magic...</LoaderSubText>
                  </LoaderText>
                </LoaderDialog>
              </LoaderContainer>
            )}

            {/* Error State - Generating Itinerary */}
            {itineraryMutation.isError && (
              <ErrorState>
                <XCircleIcon style={{ width: '24px', height: '24px' }} />
                {apiUtils.handleError(itineraryMutation.error).message}
              </ErrorState>
            )}
          </Container>
        )}

        {/* Budget Planner Modal */}
        <BudgetPlanner
          itineraryData={itineraryData || fetchedItinerary}
          isOpen={showBudgetPlanner}
          onClose={() => setShowBudgetPlanner(false)}
        />

        {/* User Preferences Modal */}
        <UserPreferencesModal
          isOpen={showPreferencesModal}
          onClose={() => setShowPreferencesModal(false)}
          onSave={(prefs) => setUserPreferences(prefs)}
          initialPreferences={userPreferences}
        />

        {/* Itinerary Generation Modal with real-time progress */}
        {showGenerationModal && generationJobId && (
          <ItineraryGenerationModal
            jobId={generationJobId}
            estimatedDuration={estimatedDuration}
            isSurprise={!!location.state?.isSurprise}
            onClose={() => {
              setShowGenerationModal(false);
              setGenerationJobId(null);
            }}
          />
        )}

        {/* Floating Action Button (FAB) for Mobile - Scrolls to the main form */}
        {isAuthenticated && viewMode === 'myItineraries' && !resolvedId && !itineraryData && isMobile && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              setViewMode('default');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{
              position: 'fixed',
              bottom: '100px',
              right: '20px',
              width: '52px',
              height: '52px',
              borderRadius: '50%',
              background: '#2563eb',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
            }}
          >
            <SparklesIcon style={{ width: '22px', height: '22px', color: 'white' }} />
          </motion.button>
        )}

        {/* Old form modal removed — FAB now navigates to the main scenic form */}
      </PageContainer>
    </>
  );
}

export default ItineraryPage;


